import React, { useState } from 'react';
import { ShoppingCart, Plus, Trash2, Check, Store, Tag } from 'lucide-react';
import { api } from '../services/api';
import { useToast } from './Toast';

const ShoppingListView = ({ items, setItems, projectId }) => {
    const { showToast } = useToast();
    const [newItem, setNewItem] = useState({ item_name: '', quantity: '', store: 'General', category: 'Need Now', cost_est: 0, priority: 'Normal' });

    const handleAdd = async () => {
        if (!newItem.item_name) return;
        if (!projectId) return showToast('Error: No project selected', 'error');
        const tempId = Date.now();
        const optimistic = { ...newItem, id: tempId, checked: 0 };
        setItems([...items, optimistic]);
        setNewItem({ ...newItem, item_name: '', quantity: '' }); // Keep category/store for rapid entry
        try { await api.addShoppingItem(projectId, newItem); showToast('Added'); } 
        catch (e) { showToast('Save failed', 'error'); setItems(items); }
    };

    const toggleCheck = async (item) => {
        const newVal = item.checked ? 0 : 1;
        setItems(items.map(i => i.id === item.id ? { ...i, checked: newVal } : i));
        await api.updateShoppingItem(item.id, { checked: newVal });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Remove item?')) {
            setItems(items.filter(i => i.id !== id));
            await api.deleteShoppingItem(id);
        }
    };

    // Group by Category, then Store
    const grouped = items.reduce((acc, item) => {
        const cat = item.category || 'Uncategorized';
        if (!acc[cat]) acc[cat] = {};
        const st = item.store || 'General';
        if (!acc[cat][st]) acc[cat][st] = [];
        acc[cat][st].push(item);
        return acc;
    }, {});

    return (
        <div className="space-y-6 pb-20">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
                <h3 className="font-bold text-green-800 mb-2 flex items-center gap-2"><ShoppingCart size={20}/> Add Item</h3>
                <div className="flex flex-col md:flex-row gap-2">
                    <input className="flex-[2] border rounded p-2" placeholder="Item Name (e.g. 2x4 Studs)" value={newItem.item_name} onChange={e => setNewItem({...newItem, item_name: e.target.value})} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
                    <input className="flex-1 border rounded p-2" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem({...newItem, quantity: e.target.value})} />
                    <input className="flex-1 border rounded p-2" placeholder="Category" list="cats" value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} />
                    <datalist id="cats"><option>Need Now</option><option>Future</option><option>Tools</option><option>Finishing</option></datalist>
                    <input className="flex-1 border rounded p-2" placeholder="Store" list="stores" value={newItem.store} onChange={e => setNewItem({...newItem, store: e.target.value})} />
                    <datalist id="stores"><option>Home Depot</option><option>Lowe's</option><option>Amazon</option><option>Menards</option></datalist>
                    <button onClick={handleAdd} className="bg-green-600 text-white px-4 py-2 rounded font-bold hover:bg-green-700">Add</button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.keys(grouped).sort().map(cat => (
                    <div key={cat} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gray-100 px-4 py-2 border-b font-bold text-gray-700 flex items-center gap-2"><Tag size={14}/> {cat}</div>
                        {Object.keys(grouped[cat]).sort().map(store => (
                            <div key={store} className="border-b last:border-0">
                                <div className="bg-gray-50 px-4 py-1 text-xs font-bold text-gray-500 uppercase flex items-center gap-1"><Store size={10}/> {store}</div>
                                <div className="divide-y">
                                    {grouped[cat][store].map(item => (
                                        <div key={item.id} className={`p-3 flex justify-between items-center ${item.checked ? 'bg-gray-50 opacity-50' : 'bg-white'}`}>
                                            <div className="flex items-center gap-3">
                                                <button onClick={() => toggleCheck(item)} className={`w-5 h-5 rounded border flex items-center justify-center ${item.checked ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'}`}>
                                                    {item.checked ? <Check size={14}/> : null}
                                                </button>
                                                <div>
                                                    <div className={`text-sm font-medium ${item.checked ? 'line-through text-gray-500' : 'text-gray-800'}`}>{item.item_name}</div>
                                                    <div className="text-xs text-gray-500">{item.quantity}</div>
                                                </div>
                                            </div>
                                            <button onClick={() => handleDelete(item.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={16}/></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
                {items.length === 0 && <div className="col-span-full text-center py-10 text-gray-400">Shopping list is empty.</div>}
            </div>
        </div>
    );
};
export default ShoppingListView;